import { createContext, useContext, useState, useEffect } from 'react';
import { authApi, setFallbackToken, getFallbackToken, clearFallbackToken, describirErrorServidor } from './api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Motivo por el que no se pudo verificar la sesión (servidor caído, timeout...). null si fue un simple "no logueado".
  const [sessionError, setSessionError] = useState(null);

  useEffect(() => {
    // Check session: httpOnly cookie is sent automatically,
    // Bearer token from sessionStorage is sent as fallback by axios interceptor
    const teniaToken = !!getFallbackToken();
    authApi.me()
      .then((res) => {
        // Si el proxy del preview responde con una página HTML (entorno despertando), no es un usuario válido
        const u = res.data;
        if (u && typeof u === 'object' && u.email) setUser(u);
        else { setUser(null); clearFallbackToken(); setSessionError('No se pudo verificar tu sesión: el servidor devolvió una respuesta no válida. Reintenta en unos segundos.'); }
      })
      .catch((err) => {
        setUser(null);
        clearFallbackToken();
        if (err?.response?.status !== 401) setSessionError(`No se pudo verificar tu sesión: ${describirErrorServidor(err)}`);
        else if (teniaToken) setSessionError('Tu sesión expiró o fue cerrada. Inicia sesión nuevamente.');
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await authApi.login({ email, password });
    const { access_token, user: userData } = res.data;
    // Save token as fallback for browsers that block httpOnly cookies
    if (access_token) {
      setFallbackToken(access_token);
    }
    setSessionError(null);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, nombre, rol = 'operador') => {
    const res = await authApi.register({ email, password, nombre, rol });
    return res.data;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch { /* ignore */ }
    clearFallbackToken();
    setUser(null);
  };

  const isAdmin = () => user?.rol === 'admin';
  const isSupervisor = () => user?.rol === 'supervisor' || user?.rol === 'admin';
  const isTerreno = () => user?.rol === 'terreno';

  return (
    <AuthContext.Provider value={{ user, loading, sessionError, login, register, logout, isAdmin, isSupervisor, isTerreno }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
